import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Header, Icon, Modal } from 'semantic-ui-react';
import { getEmail } from '../../services/Auth';
import { requestEmailChange, verifyEmailChange } from '../../services/Settings';

const EXPIRY_MS = 15 * 60 * 1000;

function ChangeEmailModal({ open, setOpen }) {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [requestStartTime, setRequestStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (requestStartTime && step === 2) {
      timerRef.current = setInterval(() => {
        const remaining = EXPIRY_MS - (Date.now() - requestStartTime);
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          setTimeLeft(0);
          setStep(1);
          setMessage('Délai expiré, recommencez la procédure.');
        } else {
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [requestStartTime, step]);

  const handleClose = () => {
    setStep(1);
    setNewEmail('');
    setCode('');
    setMessage('');
    setSending(false);
    setRequestStartTime(null);
    setTimeLeft(null);
    clearInterval(timerRef.current);
    setOpen(false);
  };

  const handleRequest = () => {
    setMessage('');
    if (!newEmail || !newEmail.includes('@')) {
      setMessage('Adresse email invalide');
      return;
    }
    if (newEmail === getEmail()) {
      setMessage("La nouvelle adresse doit être différente de l'actuelle");
      return;
    }
    setSending(true);
    requestEmailChange({ newEmail })
      .then((response) => {
        setSending(false);
        if (response.status === 200 && response.data.success) {
          setRequestStartTime(Date.now());
          setStep(2);
        } else {
          setMessage(`Échec : ${response.data.message}`);
        }
      })
      .catch((e) => {
        setSending(false);
        setMessage(`Échec : ${e.message}`);
      });
  };

  const handleVerify = () => {
    setMessage('');
    if (!code || code.length !== 6) {
      setMessage('Le code doit faire 6 chiffres');
      return;
    }
    if (requestStartTime && Date.now() - requestStartTime >= EXPIRY_MS) {
      setStep(1);
      setMessage('Délai expiré, recommencez la procédure.');
      return;
    }
    setSending(true);
    verifyEmailChange({ code })
      .then((response) => {
        setSending(false);
        if (response.status === 200 && response.data.success) {
          setMessage(
            `Un lien de confirmation a été envoyé sur ${newEmail}. Consultez votre boîte mail (temps restant : ${timeLeft}).`
          );
          setCode('');
        } else {
          setMessage(`Échec : ${response.data.message}`);
        }
      })
      .catch((e) => {
        setSending(false);
        setMessage(`Échec : ${e.message}`);
      });
  };

  return (
    <Modal basic onClose={handleClose} onOpen={() => setOpen(true)} open={open}>
      <Header icon>
        <Icon name="mail" color="teal" />
        <br />
        Changer d&apos;adresse email
      </Header>
      <Modal.Content>
        <Modal.Description>
          {step === 1 && (
            <>
              <Header as="h5" color="teal">
                Un code de vérification sera envoyé sur votre adresse email actuelle ({getEmail()}).
              </Header>
              <Form>
                <Form.Group widths="equal">
                  <Form.Input
                    fluid
                    type="email"
                    placeholder="Nouvelle adresse email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={sending}
                  />
                </Form.Group>
              </Form>
            </>
          )}
          {step === 2 && (
            <>
              <Header as="h5" color="teal">
                Un code à 6 chiffres a été envoyé sur {getEmail()}.{' '}
                {timeLeft && `Temps restant : ${timeLeft}`}
              </Header>
              <Form>
                <Form.Group widths="equal">
                  <Form.Input
                    fluid
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Code à 6 chiffres"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    disabled={sending}
                  />
                </Form.Group>
              </Form>
            </>
          )}
          {message && (
            <div
              style={{
                marginTop: '1em',
                color: message.startsWith('Un lien') ? '#21ba45' : '#db2828',
              }}
            >
              {message}
            </div>
          )}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button basic color="grey" onClick={handleClose} inverted>
          <Icon name="remove" /> Annuler
        </Button>
        {step === 1 && (
          <Button onClick={handleRequest} color="blue" inverted disabled={sending}>
            <Icon name="send" /> Envoyer le code
          </Button>
        )}
        {step === 2 && (
          <Button onClick={handleVerify} color="green" inverted disabled={sending}>
            <Icon name="checkmark" /> Vérifier
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}

export default ChangeEmailModal;
